import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import MuiButton from '@material-ui/core/Button';

import Progress from 'components/common/Progress';

import pxToRem from 'styles/pxToRem';
import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  rootSizeXl: {
    fontSize: pxToRem(16),
    paddingTop: 10,
    paddingBottom: 10,
    minWidth: 88,
  },
  rootSizeLg: {
    paddingTop: 11,
    paddingBottom: 11,
    minWidth: 88,
  },
  rootSizeSm: {
    fontSize: pxToRem(12),
    letterSpacing: 0,
    paddingTop: 6,
    paddingBottom: 6,
    minHeight: 32,
  },
  rootUnderline: {
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  rootNav: {
    minHeight: 30,
    paddingTop: 6,
    paddingBottom: 6,
    textTransform: 'none',
    borderRadius: 20,
    color: theme.palette.grey[700],
  },
  rootNavActive: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary[500],
    '&:hover': {
      backgroundColor: theme.palette.primary[700],
    },
  },
  rootDisableGutters: {
    padding: 0,
  },
  rootDisableShadow: {
    boxShadow: 'none',
    '&:active': {
      boxShadow: 'none',
    },
  },
  rootRaisedColorWarning: {
    color: theme.palette.common.white,
    border: 'none',
    fontWeight: '700',
    backgroundColor: theme.palette.validation[500],
    '&:hover': {
      backgroundColor: theme.palette.validation[900],
    },
    '&:after': {
      display: 'none',
    },
    '&$rootRaisedColorWarningDisabled': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.validation[300],
    },
  },
  rootRaisedColorSecondary: {
    color: theme.palette.primary[500],
    fontWeight: '700',
    backgroundColor: theme.palette.common.white,
    border: 'none',
    '&:hover': {
      backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
      '&:after': {
        opacity: 1,
      },
    },
    '&:after': {
      border: `2px solid ${theme.palette.primary[500]}`,
    },
    '&$rootRaisedColorSecondaryDisabled': {
      color: theme.palette.primary[500],
      backgroundColor: theme.palette.common.white,
    },
  },
  rootRaisedColorWarningDisabled: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.validation[300],
  },
  rootRaisedColorSecondaryDisabled: {
    color: theme.palette.primary[500],
    backgroundColor: theme.palette.common.white,
  },
});

const Button = ({
  className,
  classes,
  children,
  nav,
  size,
  color,
  variant,
  underline,
  href,
  download,
  onClick,
  history,
  disableGutters,
  disableShadow,
  exact,
  target,
  staticContext,
  loading,
  ...otherProps
}) => (
  <MuiButton
    {...otherProps}
    {...download ? { download } : {}}
    target={target}
    {...['warning', 'secondary'].indexOf(color) !== -1 ? {} : { color }}
    variant={variant}
    classes={{
      root: classNames(className, {
        [classes.rootSizeXl]: size === 'xl',
        [classes.rootSizeLg]: size === 'lg',
        [classes.rootSizeSm]: size === 'sm',
        [classes.rootRaisedColorWarning]: color === 'warning' && variant === 'raised',
        [classes.rootRaisedColorSecondary]: color === 'secondary' && variant === 'raised',
        [classes.rootUnderline]: underline,
        [classes.rootNav]: nav,
        [classes.rootNavActive]: nav
          && ((!exact && history.location.pathname.indexOf(href) !== -1)
            || (exact && history.location.pathname === href)),
        [classes.rootDisableGutters]: disableGutters,
        [classes.rootDisableShadow]: disableShadow,
      }),
      disabled: classNames({
        [classes.rootRaisedColorWarningDisabled]: color === 'warning' && variant === 'raised',
        [classes.rootRaisedColorSecondaryDisabled]: color === 'secondary' && variant === 'raised',
      }),
    }}
    {...href && staticContext ? { href } : {}}
    onClick={href && !download && target !== '_blank'
      ? (event) => {
        event.preventDefault();
        onClick();
        if (href !== history.location.pathname) {
          history.push(href);
        }
      }
      : onClick
    }
  >
    {children}
    {loading &&
      <Progress
        size={28}
        className={classes.progress}
        color={color === 'primary' ? 'secondary' : 'primary'}
      />
    }
  </MuiButton>
);

Button.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  color: PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary', 'warning']),
  variant: PropTypes.oneOf(['flat', 'raised', 'fab']),
  nav: PropTypes.bool,
  underline: PropTypes.bool,
  href: PropTypes.string,
  download: PropTypes.string,
  onClick: PropTypes.func,
  disableGutters: PropTypes.bool,
  staticContext: PropTypes.bool,
  disableShadow: PropTypes.bool,
  exact: PropTypes.bool,
  loading: PropTypes.bool,
  target: PropTypes.oneOf(['_blank', '_self']),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    rootSizeXl: PropTypes.string.isRequired,
    rootSizeLg: PropTypes.string.isRequired,
    rootSizeSm: PropTypes.string.isRequired,
    rootRaisedColorWarning: PropTypes.string.isRequired,
    rootRaisedColorSecondary: PropTypes.string.isRequired,
    rootRaisedColorWarningDisabled: PropTypes.string.isRequired,
    rootRaisedColorSecondaryDisabled: PropTypes.string.isRequired,
    rootUnderline: PropTypes.string.isRequired,
    rootNav: PropTypes.string.isRequired,
    rootDisableGutters: PropTypes.string.isRequired,
    rootDisableShadow: PropTypes.string.isRequired,
  }).isRequired,
};

Button.defaultProps = {
  size: 'md',
  color: 'default',
  variant: 'flat',
  nav: false,
  underline: false,
  className: '',
  href: null,
  download: null,
  staticContext: true,
  disableGutters: false,
  disableShadow: false,
  exact: false,
  loading: false,
  target: '_self',
  onClick: () => {},
};

export default withRouter(
  withStyles(styles)(Button),
);



// WEBPACK FOOTER //
// ./src/components/common/Button.js